
import { useMovieContext } from "@/context";
import { Movie } from "@/types/movie";
import { logRender, logState, logAction, isDebugMovie } from "@/utils/debugLogger";

// Import our specialized hooks
import { useCardVisualState } from "./movie-card/useCardVisualState";
import { usePendingActionTracking } from "./movie-card/usePendingActionTracking";
import { useCardInteractions } from "./movie-card/useCardInteractions";
import { useCardProperties } from "./movie-card/useCardProperties";
import { useForceRender } from "./movie-card/useForceRender";

interface UseMovieCardResult {
  isHovered: boolean;
  pendingAction: any;
  showAsFavorite: boolean;
  showAsPlanToWatch: boolean;
  getRemainingTime: () => number;
  handleOpenDialog: () => void;
  handleToggleFavorite: (e: React.MouseEvent) => void;
  handleTogglePlanToWatch: (e: React.MouseEvent) => void;
  setIsHovered: (value: boolean) => void;
}

export const useMovieCard = (
  movie: Movie, 
  currentTab?: string,
  onOpenMovieDetails?: (tmdbId: number) => void,
  isSearchResult = false
): UseMovieCardResult => {
  const { 
    pendingActions, 
    toggleFavorite, 
    togglePlanToWatch, 
    getVisualState, 
    viewMode,
    commitToggleFavorite,
    commitTogglePlanToWatch
  } = useMovieContext();

  // Use TMDB ID as the lookup key
  const tmdbId = movie.tmdbId || 0;

  // Use our specialized hooks
  const { 
    pendingAction, 
    shouldDebug 
  } = usePendingActionTracking(movie, pendingActions, currentTab, isSearchResult);

  const { 
    hovered, 
    setHovered, 
    getRemainingTime 
  } = useCardVisualState(movie.title, undefined, shouldDebug);

  // FIXED: Simplified wrapper functions for the unified system
  const wrappedToggleFavorite = async (movieId: number, currentSection?: string) => {
    await toggleFavorite(movieId, currentSection);
  };

  const wrappedTogglePlanToWatch = async (movieId: number, currentSection?: string) => {
    await togglePlanToWatch(movieId, currentSection);
  };

  // Create wrapper for onOpenMovieDetails that properly converts TMDB ID
  const wrappedOnOpenMovieDetails = async (movieId: string) => {
    if (onOpenMovieDetails && movie.tmdbId) {
      onOpenMovieDetails(movie.tmdbId);
    }
  };

  // FIXED: Use simplified card interactions without dual handler logic
  const { 
    handleOpenDialog, 
    handleToggleFavorite, 
    handleTogglePlanToWatch 
  } = useCardInteractions(
    movie, 
    currentTab, 
    isSearchResult, 
    shouldDebug, 
    !!pendingAction,
    wrappedOnOpenMovieDetails,
    commitToggleFavorite,
    commitTogglePlanToWatch,
    wrappedToggleFavorite,
    wrappedTogglePlanToWatch
  );

  // Determine visual states using the unified getVisualState function
  const favoriteState = getVisualState(tmdbId, 'favorite');
  const planToWatchState = getVisualState(tmdbId, 'planToWatch');
  
  const showAsFavorite = typeof favoriteState === 'boolean' ? favoriteState : favoriteState.currentValue;
  const showAsPlanToWatch = typeof planToWatchState === 'boolean' ? planToWatchState : planToWatchState.currentValue;

  // Track visual properties
  const { generateCardKey } = useCardProperties(
    movie.title, 
    showAsFavorite, 
    showAsPlanToWatch, 
    tmdbId.toString(), 
    shouldDebug
  );

  // Set up force rendering effect
  useForceRender(
    movie.title, 
    viewMode, 
    pendingActions, 
    currentTab, 
    shouldDebug, 
    !!pendingAction
  );

  return {
    isHovered: hovered,
    pendingAction,
    showAsFavorite,
    showAsPlanToWatch,
    getRemainingTime,
    handleOpenDialog,
    handleToggleFavorite,
    handleTogglePlanToWatch,
    setIsHovered: setHovered
  };
};
